import { chakra } from '@chakra-ui/react';
import { forwardRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Icon, type LucideIcon } from '../icons/Icon';
import { focusRing } from '../primitives/_shared';

export type BannerTone = 'info' | 'success' | 'warning' | 'danger' | 'brand';

/**
 * Tone recipe — a full *subtle* tonal surface (NO side-stripe left border) plus an
 * AA-verified foreground. `fg` cascades as `currentColor` to the icon, message and the
 * dismiss control; the hairline border reuses the same foreground at low alpha so the
 * banner reads as one calm block rather than a warning bar.
 */
const toneStyle: Record<BannerTone, { bg: string; fg: string; border: string }> = {
  info: { bg: 'status.infoSubtle', fg: 'status.infoFg', border: 'status.infoFg/20' },
  success: { bg: 'status.successSubtle', fg: 'status.successFg', border: 'status.successFg/20' },
  warning: { bg: 'status.warningSubtle', fg: 'status.warningFg', border: 'status.warningFg/20' },
  danger: { bg: 'status.dangerSubtle', fg: 'status.dangerFg', border: 'status.dangerFg/20' },
  brand: { bg: 'brand.subtle', fg: 'brand.fg', border: 'brand.fg/20' },
};

const Root = chakra('div', {
  base: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '3',
    borderRadius: 'lg',
    borderWidth: '1px',
    px: '4',
    py: '3',
    fontFamily: 'body',
  },
});

const Message = chakra('div', {
  base: {
    flex: '1',
    minWidth: 0,
    fontSize: 'sm',
    lineHeight: 'snug',
    color: 'currentColor',
  },
});

const DismissButton = chakra('button', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSize: '6',
    mt: '-0.5',
    mr: '-1',
    flexShrink: 0,
    borderRadius: 'md',
    color: 'currentColor',
    bg: 'transparent',
    border: 'none',
    cursor: 'pointer',
    opacity: 0.75,
    transitionProperty: 'opacity',
    transitionDuration: 'fast',
    _hover: { opacity: 1 },
    ...focusRing,
  },
});

export interface BannerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color' | 'title'> {
  /** Semantic tone — drives the subtle fill + AA foreground. */
  tone?: BannerTone;
  /** Optional leading icon (a lucide glyph), coloured to the tone. */
  icon?: LucideIcon;
  /** When provided, renders a trailing dismiss (×) button that calls this. */
  onDismiss?: () => void;
  /** Optional trailing action slot (e.g. a link or small button). */
  action?: ReactNode;
  children: ReactNode;
}

/**
 * A Banner (a.k.a. Callout): an inline, non-modal message on a full subtle tonal surface.
 * Tones info/success/warning/danger/brand; optional leading icon, optional dismiss button
 * and optional action slot. Semantic tokens only — the fill is AA against its foreground in
 * both light and dark. Use for confirmations, orientation notes and inline warnings; use a
 * `Dialog` for anything that must interrupt.
 */
export const Banner = forwardRef<HTMLDivElement, BannerProps>(function Banner(
  { tone = 'info', icon, onDismiss, action, children, role = 'status', ...rest },
  ref,
) {
  const t = toneStyle[tone];
  return (
    <Root ref={ref} role={role} bg={t.bg} color={t.fg} borderColor={t.border} {...rest}>
      {icon && (
        <chakra.span display="inline-flex" flexShrink={0} mt="0.5">
          <Icon as={icon} size="md" tone="current" />
        </chakra.span>
      )}
      <Message>{children}</Message>
      {action && (
        <chakra.span display="inline-flex" flexShrink={0} alignItems="center">
          {action}
        </chakra.span>
      )}
      {onDismiss && (
        <DismissButton type="button" aria-label="Dismiss" onClick={onDismiss}>
          <X size={16} strokeWidth={2} aria-hidden />
        </DismissButton>
      )}
    </Root>
  );
});
